import { useContext, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router';

import { useSearch } from '@/apis/searches';
import { NotRenewingContext } from '@/App';
import {
  restartKeyAtom,
  searchTemplateDataAtom,
  searchTemplateDataFromTreeUrlPayload,
  type TreeUrlPayload,
} from '@/state/searchTemplate/searchTemplateData';

import { hasNotRenewing, parseJSONWithoutException } from '@/util';
import { usePrecacheUserContactPerms } from '@hooks/useCaching';
import { CircularProgressLoadingPage } from '@pages/CircularProgressLoadingPage';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import AdvancedSearchListCreatorPage from '.';
import { useSearchTree } from './JsonTreeBuilder';
import { isValidationDialogOpenAtom, templateValidationErrorsAtom } from './ValidationDialog';

export const AdvancedSearchLoader = ({
  searchTreeBuilderTestId,
  previewSectionTestId,
}: {
  searchTreeBuilderTestId?: string;
  previewSectionTestId?: string;
}) => {
  const { searchId } = useParams();

  const [searchParamData, setSearchParamData] = useSearchParams();
  const searchParamTree = searchParamData.get('tree');
  const parsedTreePayload: TreeUrlPayload | null = searchParamTree
    ? (parseJSONWithoutException(searchParamTree) as TreeUrlPayload | null)
    : null;
  const searchTemplateDataFromUrl = searchTemplateDataFromTreeUrlPayload(parsedTreePayload);

  const { data: searchData, isFetching: isLoadingSearch } = useSearch(Number(searchId));
  const [searchTemplateData, setSearchTemplateData] = useAtom(searchTemplateDataAtom);
  const restartKey = useAtomValue(restartKeyAtom);
  const setTemplateValidationErrors = useSetAtom(templateValidationErrorsAtom);
  const setIsValidationDialogOpen = useSetAtom(isValidationDialogOpenAtom);
  const { setNotRenewingPresent } = useContext(NotRenewingContext);

  usePrecacheUserContactPerms();

  const { data: initialLoadedTree, isFetching } = useSearchTree(
    searchTemplateData
      ? searchTemplateData.initialTree
      : searchData?.type === 'Advanced'
        ? searchData?.data
        : undefined
  );

  const isNotRenewing = searchTemplateData
    ? hasNotRenewing(searchTemplateData.initialTree)
    : searchData?.type === 'Advanced'
      ? hasNotRenewing(searchData?.data)
      : false;

  // When the component unmounts, reset the atoms to their initial values
  // This prevents the validation state of each dialog from leaking
  useEffect(() => {
    return () => {
      setSearchTemplateData(undefined);
      setTemplateValidationErrors([]);
      setIsValidationDialogOpen(false);
    };
  }, [setSearchTemplateData, setTemplateValidationErrors, setIsValidationDialogOpen]);

  useEffect(() => {
    if (searchTemplateDataFromUrl && !searchTemplateData) {
      setSearchTemplateData(searchTemplateDataFromUrl);
      setSearchParamData({});
    }
  }, [searchTemplateDataFromUrl, searchTemplateData, setSearchParamData, setSearchTemplateData]);

  const isWaitingForFinalStatus =
    isFetching || isLoadingSearch || !searchData || !initialLoadedTree;

  useEffect(() => {
    if (!isWaitingForFinalStatus) {
      setNotRenewingPresent(isNotRenewing);
    }
  }, [isNotRenewing, isWaitingForFinalStatus, setNotRenewingPresent]);

  return isWaitingForFinalStatus ? (
    <CircularProgressLoadingPage />
  ) : (
    <>
      <AdvancedSearchListCreatorPage
        key={`advanced-search-${restartKey}`}
        loadedTree={initialLoadedTree}
        loadedMetadata={
          searchTemplateData?.templateName !== undefined
            ? {
                name: searchTemplateData?.templateName,
                description: '',
                id: searchTemplateData?.templateId ?? 0,
              }
            : searchData?.metadata
        }
        previewSectionTestId={previewSectionTestId}
        searchTreeBuilderTestId={searchTreeBuilderTestId}
      />
    </>
  );
};
